import moment from 'moment';
import { useMemo } from 'react';
import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import Iconmoon from '@deprecated-ui/common/Iconmoon';
import color from '@deprecated-ui/_style/color';
import shallow from 'zustand/shallow';

// Store
import usePortfolioDetailStore from 'global/Securities/stores/usePortfolioDetailStore';
import useEmittenInfoStore from 'global/Securities/stores/useEmittenInfoStore';
import tradingDataStore from 'lib/stores/tradingData';

import useLivePriceUpdater from 'global/Securities/hooks/useLivePriceUpdater';
import useIsUserSyariah from 'global/TradingModal/hooks/useIsUserSyariah';

// Utils
import { formatNumber, formatPercentage } from 'utils/number';
import { getColorBasedOnNumber } from 'utils/general';
import { trackAutoSellOrderSelected } from 'lib/analytics/autoOrder';
import { generateUpdatedPortfolioItem } from 'global/Securities/utils/formula';

// Constants
import { ORDER_TYPE } from 'constants/trading';
import { MODAL_TYPE } from 'global/TradingModal/RealTradingModal/types';
import { StockType } from 'global/Securities/types/stockType';
import { AutoOrderProduct } from 'global/Securities/types/autoOrder';
import { checkIsAutoOrderTncAgreed } from 'global/TradingModal/RealTradingModal/TransactionModal/utils';

// Component
import Image from 'global/Image';
import If from 'global/ErrorHandlers/If';
import SkeletonWrapper from 'global/Securities/components/SkeletonWrapper';
import NonSyariahBanner from 'features/syariah/components/NonSyariahBanner';
import { DataSource, InfoTable } from '../DetailModal/InfoTable';
import ToHistoryButton from './ToHistoryButton';
import PortfolioAllocation from './PortfolioAllocation';

import {
  PortfolioSkeletonBlock,
  PortfolioInfoTable,
  SectionButton,
} from './styled';

import OrderList from './OrderList';

interface ModalDetailProps {
  symbol?: string;
  username?: string;
  stockType?: StockType;
  isDayTrade?: boolean;
  onAutoOrderClick?(modalType: string): void;
}

const MODAL_TYPES = {
  detail: 'detail',
  exercise: ORDER_TYPE.EXERCISE,
  sell: ORDER_TYPE.SELL,
  history: 'history',
  tnc: MODAL_TYPE.AUTOBUY_TNC,
  autoorder: 'auto-order',
};

const ModalDetail = ({
  symbol,
  username,
  stockType,
  isDayTrade = false,
  onAutoOrderClick = () => {},
}: ModalDetailProps) => {
  const { portfolioData, exerciseType, configFormula } = tradingDataStore(
    (s) => ({
      portfolioData: s.portfolio.data,
      exerciseType: s.portfolio.data.result?.find(
        (item) => item.symbol === symbol,
      )?.exercise_type,
      configFormula: s.configFormula.data,
    }),
    shallow,
  );
  const {
    portfolioDetail,
    portfolioDetailDayTrade,
    isPortfolioDetailLoading,
    updatePortfolioDetail,
  } = usePortfolioDetailStore(
    (s) => ({
      portfolioDetail: s.data,
      portfolioDetailDayTrade: s.dayTrade,
      isPortfolioDetailLoading: s.isLoading,
      updatePortfolioDetail: s.updatePortfolioDetail,
    }),
    shallow,
  );

  const isCompanyLoading = useEmittenInfoStore((s) => s.isLoading);
  const isUserSyariah = useIsUserSyariah();

  const selectedPortfolioDetail = isDayTrade
    ? portfolioDetailDayTrade
    : portfolioDetail;

  const {
    exercise_data: exerciseData,
    balance_share: balanceShare,
    balance_lot: balanceLot,
    available_share: availableShare,
    available_lot: availableLot,
    stock_on_hand: stockOnHand,
    price_average_fee: priceAverageFee,
    price_latest: priceLatest,
    unrealised_marketvalue: unrealisedMarketValue,
    unrealised_profitloss: unrealisedProfitloss,
    unrealised_gain: unrealisedGain,
    show_create_smartorder: showCreateSmartOrder,
    total,
    is_sharia: isStockSyariah,
  } = selectedPortfolioDetail || {};

  const { equity } = portfolioData;

  const isShowAutoOrder = useMemo(
    () => showCreateSmartOrder && stockType === StockType.Saham && !isDayTrade,
    [showCreateSmartOrder, stockType, isDayTrade],
  );

  const handleAutoOrderClick = () => {
    trackAutoSellOrderSelected(symbol);
    onAutoOrderClick(
      checkIsAutoOrderTncAgreed(username, AutoOrderProduct.AutoSell)
        ? MODAL_TYPES.autoorder
        : MODAL_TYPES.tnc,
    );
  };

  useLivePriceUpdater(
    {
      symbol,
      page: 'portfolio_detail',
    },
    ({ lastprice }) => {
      const updatedData = generateUpdatedPortfolioItem(
        configFormula,
        lastprice,
        balanceLot,
        total,
      );

      updatePortfolioDetail(
        {
          price_latest: lastprice,
          ...updatedData,
        },
        false,
        isDayTrade,
      );
    },
  );

  const portfolioDataSource: DataSource[] = useMemo(() => {
    const isRightOrWarran = [StockType.Waran, StockType.Right].includes(
      stockType,
    );
    return [
      {
        label: (
          <Text data-cy={`balance-${isRightOrWarran ? 'share' : 'lot'}-label`}>
            {`Balance ${isRightOrWarran ? 'Shares' : 'Lot'}`}
          </Text>
        ),
        value: (
          <Text
            data-cy={`balance-${isRightOrWarran ? 'share' : 'lot'}-content`}
          >
            {Number(isRightOrWarran ? balanceShare : balanceLot || 0)}
          </Text>
        ),
      },
      {
        label: (
          <Text
            data-cy={`available-${isRightOrWarran ? 'share' : 'lot'}-label`}
          >
            {isRightOrWarran ? 'Available Shares' : 'Available Lot'}
          </Text>
        ),
        value: (
          <Text
            data-cy={`available-${isRightOrWarran ? 'share' : 'lot'}-content`}
          >
            {Number(isRightOrWarran ? availableShare : availableLot || 0)}
          </Text>
        ),
      },
      ...(isRightOrWarran
        ? [
            {
              label: <Text data-cy="stock-on-hand-label">Stock on Hand</Text>,
              value: (
                <Text data-cy="stock-on-hand-content">
                  {Number(stockOnHand)}
                </Text>
              ),
            },
          ]
        : []),
      {
        label: <Text data-cy="average-price-label">Average Price</Text>,
        value: (
          <Text data-cy="average-price-content">
            {formatNumber(priceAverageFee, true, '0,0.00')}
          </Text>
        ),
      },
      {
        label: <Text data-cy="current-price-label">Current Price</Text>,
        value: (
          <Text data-cy="current-price-content">
            {formatNumber(priceLatest)}
          </Text>
        ),
      },
      {
        label: <Text data-cy="invested-label">Invested</Text>,
        value: <Text data-cy="invested-content">{formatNumber(total)}</Text>,
      },
      {
        label: <Text data-cy="market-value-label">Market Value</Text>,
        value: (
          <Text data-cy="market-value-content">
            {formatNumber(unrealisedMarketValue)}
          </Text>
        ),
      },
      {
        label: <Text data-cy="potential-pnl-label">Potential P&L</Text>,
        value: (
          <>
            <Text
              data-cy="potential-pnl-content"
              color={getColorBasedOnNumber(unrealisedProfitloss)}
            >
              {unrealisedProfitloss > 0 ? '+' : ''}
              {formatNumber(unrealisedProfitloss)}
            </Text>
          </>
        ),
      },
      {
        label: <Text data-cy="percentage-label">Percentage</Text>,
        value: (
          <Text
            data-cy="percentage-content"
            color={getColorBasedOnNumber(unrealisedGain)}
          >
            {unrealisedGain > 0 ? '+' : ''}
            {formatPercentage(unrealisedGain * 100, false, '0.00', true)}
          </Text>
        ),
      },
    ];
  }, [
    balanceShare,
    balanceLot,
    availableShare,
    availableLot,
    stockOnHand,
    priceAverageFee,
    priceLatest,
    total,
    unrealisedMarketValue,
    unrealisedProfitloss,
    unrealisedGain,
    stockType,
  ]);

  const exerciseDataSource: DataSource[] = useMemo(() => {
    if (!exerciseData) return [];

    return [
      {
        label: <Text>Exercise End</Text>,
        value: (
          <Text>
            {moment(exerciseData.exercise_end_date, 'DD-MM-YYYY').format(
              'DD MMM YYYY',
            )}
          </Text>
        ),
      },
      {
        label: <Text>Exercise Price</Text>,
        value: <Text>{exerciseData.price}</Text>,
      },
    ];
  }, [exerciseData?.exercise_end_date, exerciseData?.price]);

  const isAutoOrderVisible = useMemo(() => {
    if (isPortfolioDetailLoading || isCompanyLoading) {
      return exerciseType !== 'Warran';
    }

    return isShowAutoOrder;
  }, [
    isPortfolioDetailLoading,
    isCompanyLoading,
    exerciseType,
    isShowAutoOrder,
  ]);

  const showNonSyariahBanner = useMemo(
    () => isUserSyariah && !isStockSyariah,
    [isUserSyariah, isStockSyariah],
  );

  const getStockType = (stock: StockType) => {
    switch (stock) {
      case StockType.Waran:
        return 'Warrant';
      case StockType.Right:
        return 'Right';
      default:
        return 'Saham';
    }
  };

  return (
    <>
      <SkeletonWrapper
        skeleton={
          <PortfolioSkeletonBlock
            height={exerciseType === 'Warran' ? 299 : 268}
          />
        }
        loading={isPortfolioDetailLoading || isCompanyLoading}
      >
        <If condition={showNonSyariahBanner}>
          <NonSyariahBanner>
            {getStockType(stockType)} telah keluar dari{' '}
            <span>Daftar Efek Syariah.</span> Segera Jual saham tersebut dari
            portfolio kamu.
          </NonSyariahBanner>
        </If>
        <PortfolioInfoTable dataSource={portfolioDataSource} />
      </SkeletonWrapper>

      <If condition={exerciseType}>
        <SkeletonWrapper
          skeleton={<PortfolioSkeletonBlock height={82} />}
          loading={isPortfolioDetailLoading}
        >
          <If condition={exerciseData}>
            <InfoTable dataSource={exerciseDataSource} />
          </If>
        </SkeletonWrapper>
      </If>

      <SkeletonWrapper
        skeleton={<PortfolioSkeletonBlock height={56} />}
        loading={isPortfolioDetailLoading}
      >
        <PortfolioAllocation
          unrealisedMarketValue={unrealisedMarketValue}
          equity={equity}
        />
      </SkeletonWrapper>

      <If condition={isAutoOrderVisible}>
        <SkeletonWrapper
          skeleton={<PortfolioSkeletonBlock height={51} />}
          loading={isPortfolioDetailLoading || isCompanyLoading}
        >
          <SectionButton
            data-cy="auto-order-button"
            className="button-unstyled full-button"
            onClick={handleAutoOrderClick}
            type="button"
          >
            <InfoTable
              dataSource={[
                {
                  label: (
                    <Flex align="center" style={{ gap: 4 }}>
                      <Image
                        style={{ width: 16 }}
                        src="/icon/plus-circle.svg"
                      />
                      <Text family="bold" color={color.primary}>
                        Auto Order
                      </Text>
                    </Flex>
                  ),
                  value: <Iconmoon className="icon-trading_back reverse" />,
                },
              ]}
            />
          </SectionButton>
        </SkeletonWrapper>
      </If>

      <OrderList symbol={symbol} isDayTrade={isDayTrade} />
      <ToHistoryButton symbol={symbol} />
    </>
  );
};

export default ModalDetail;
